import { MAX_SELECTED_TEXT_LENGTH } from './sendToParentProduct';
import type { SendToParentProductPayload } from './sendToParentProduct';

type ChangedPayloadContentType = Extract<
	SendToParentProductPayload,
	{ eventType: 'embedded-confluence.text-selection.changed' }
>['contentType'];

type ParseSendToParentProductPayloadResult =
	| {
			payload: SendToParentProductPayload;
			status: 'valid';
	  }
	| {
			status: 'invalid';
	  };

const isRecord = (payload: unknown): payload is Record<string, unknown> =>
	typeof payload === 'object' && payload !== null && !Array.isArray(payload);

const isSupportedEventType = (
	eventType: unknown,
): eventType is SendToParentProductPayload['eventType'] =>
	eventType === 'embedded-confluence.text-selection.changed' ||
	eventType === 'embedded-confluence.text-selection.cleared';

const readRequiredString = (
	payload: Record<string, unknown>,
	key: 'contentId' | 'selectedText',
): string | null => {
	const value = payload[key];
	return typeof value === 'string' && value.trim() ? value : null;
};

const readOptionalString = (
	payload: Record<string, unknown>,
	key: 'sourceTitle' | 'sourceUrl',
): string | 'invalid' | undefined => {
	const value = payload[key];
	if (value === undefined) {
		return undefined;
	}
	if (typeof value !== 'string') {
		return 'invalid';
	}

	const trimmed = value.trim();
	return trimmed ? trimmed : undefined;
};

const readOptionalStringArray = (
	payload: Record<string, unknown>,
): string[] | 'invalid' | undefined => {
	const value = payload.selectionLocalIds;
	if (value === undefined) {
		return undefined;
	}
	if (!Array.isArray(value) || value.some((localId) => typeof localId !== 'string')) {
		return 'invalid';
	}

	return value.filter((localId) => localId.trim());
};

const readOptionalContentType = (
	payload: Record<string, unknown>,
): ChangedPayloadContentType | 'invalid' | undefined => {
	const value = payload.contentType;
	if (value === undefined) {
		return undefined;
	}
	if (value === 'page' || value === 'blogpost' || value === 'whiteboard') {
		return value;
	}

	return 'invalid';
};

export const parseSendToParentProductPayload = (
	payload: unknown,
): ParseSendToParentProductPayloadResult => {
	if (!isRecord(payload)) {
		return { status: 'invalid' };
	}

	if (!isSupportedEventType(payload.eventType)) {
		return { status: 'invalid' };
	}

	const contentId = readRequiredString(payload, 'contentId');
	if (!contentId) {
		return { status: 'invalid' };
	}

	if (payload.eventType === 'embedded-confluence.text-selection.cleared') {
		return {
			payload: {
				eventType: 'embedded-confluence.text-selection.cleared',
				contentId,
			},
			status: 'valid',
		};
	}

	const selectedText = readRequiredString(payload, 'selectedText');
	if (!selectedText) {
		return { status: 'invalid' };
	}

	const selectionLocalIds = readOptionalStringArray(payload);
	if (selectionLocalIds === 'invalid') {
		return { status: 'invalid' };
	}

	const sourceTitle = readOptionalString(payload, 'sourceTitle');
	if (sourceTitle === 'invalid') {
		return { status: 'invalid' };
	}

	const sourceUrl = readOptionalString(payload, 'sourceUrl');
	if (sourceUrl === 'invalid') {
		return { status: 'invalid' };
	}

	const contentType = readOptionalContentType(payload);
	if (contentType === 'invalid') {
		return { status: 'invalid' };
	}

	return {
		payload: {
			eventType: 'embedded-confluence.text-selection.changed',
			contentId,
			selectedText: selectedText.slice(0, MAX_SELECTED_TEXT_LENGTH),
			...(selectionLocalIds?.length ? { selectionLocalIds } : {}),
			...(sourceTitle ? { sourceTitle } : {}),
			...(sourceUrl ? { sourceUrl } : {}),
			...(contentType ? { contentType } : {}),
		},
		status: 'valid',
	};
};
